import { isPresent, omit } from '@luminovo/commons';
import {
    Autocomplete,
    AutocompleteProps,
    AutocompleteRenderGroupParams,
    AutocompleteRenderOptionState,
    createFilterOptions,
    OutlinedInputProps,
    Paper,
} from '@mui/material';
import { ChevronDownIcon, PlusIcon } from 'lucide-react';
import * as React from 'react';

import { AppLinkOptions, asLinkProps } from '../../../routing/links/linkOptions';
import { colorSystem } from '../../../theme';
import { TertiaryButton } from '../../buttons';
import { Divider } from '../../Divider';
import { Flexbox } from '../../Flexbox';
import { Highlight } from '../../Highlight';
import { Text } from '../../Text';
import { generateVirtualizeProps } from '../FieldMultiSelect/VirtualizeComponents';
import { FieldText, FieldTextProps } from '../FieldText';

function DefaultRenderGroup(params: AutocompleteRenderGroupParams): React.ReactNode {
    return (
        <li key={params.key}>
            <Text
                variant="caption"
                color={colorSystem.neutral[6]}
                style={{ padding: '4px 16px 2px', textTransform: 'uppercase', letterSpacing: '0.5px' }}
            >
                {params.group}
            </Text>
            <ul style={{ padding: 0 }}>{params.children}</ul>
        </li>
    );
}

/** No `freeSolo`: it makes type inference difficult. */
export type FieldSelectProps<TValue> = Omit<
    AutocompleteProps<TValue, false, boolean | undefined, false>,
    'value' | 'options' | 'onChange' | 'renderInput' | 'size' | 'renderOption' | 'freeSolo' | 'slotProps'
> &
    Partial<Pick<FieldTextProps, 'error' | 'helperText' | 'size' | 'placeholder'>> & {
        value: TValue | null;

        options: readonly TValue[];

        onChange: (value: TValue | null) => void;

        /** Called after the value changed; `onChange` already updates it, so don't use this for that. */
        onValueChange?: (value: TValue | null) => void;

        /** A secondary label below the main one. */
        getOptionSublabel?: (option: TValue) => string | null;

        /** A badge next to the main label. */
        getOptionBadge?: (option: TValue) => React.ReactNode | null;

        /** Defaults to `getOptionLabel`; wraps MUI's `renderOption`, whose required props we can't guarantee: https://mui.com/material-ui/migration/v5-component-changes/#update-renderoption */
        renderOption?: (option: TValue, state: AutocompleteRenderOptionState) => React.ReactNode;

        /** A button at the bottom of the dropdown; with `link`, from `linkOptions()`, a link without `onClick`. */
        action?: {
            label: string;
            disabled?: boolean;
        } & ({ onClick: () => void; link?: never } | { link: AppLinkOptions; onClick?: never });

        autoFocus?: boolean;

        virtualized?: boolean;

        slotProps?: AutocompleteProps<TValue, false, boolean | undefined, false>['slotProps'] & {
            /** Forwarded to the TextField's `slotProps.input`. */
            input?: Omit<OutlinedInputProps, 'value' | 'onChange'>;
        };
    };

function FieldSelectInner<TValue>(
    {
        value,
        options,
        onChange,
        onValueChange,
        renderOption,
        getOptionLabel,
        getOptionSublabel,
        getOptionBadge,
        action,
        placeholder,
        error,
        helperText,
        autoFocus,
        virtualized = false,
        size = 'large',
        slotProps,
        'aria-label': ariaLabel,
        'aria-required': ariaRequired,
        ...rest
    }: FieldSelectProps<TValue>,
    outerRef: React.ForwardedRef<unknown>,
): JSX.Element {
    const [userTyped, setUserTyped] = React.useState(false);
    const { input: inputSlotProps, ...autocompleteSlotProps } = slotProps ?? {};

    const filterOptions = createFilterOptions<TValue>({
        stringify: (option) => `${getOptionLabel?.(option)} ${getOptionSublabel?.(option) ?? ''}`,
    });

    // Virtualization is only supported for non-grouped options.
    const isVirtualized = rest.groupBy === undefined && virtualized;
    const virtualizedProps = isVirtualized ? generateVirtualizeProps() : {};

    return (
        <Autocomplete
            ref={outerRef}
            value={value}
            options={options}
            onChange={(_, value) => {
                onChange(value);
                onValueChange?.(value);
            }}
            renderInput={({ slotProps: textFieldSlotProps, ...props }) => (
                // Hack: `value` and `onChange` are required but have no effect because
                // Autocomplete's `slotProps.htmlInput` overrides them.
                <FieldText
                    {...props}
                    type="text"
                    autoFocus={autoFocus}
                    placeholder={placeholder}
                    value={null}
                    onChange={() => {}}
                    error={error}
                    helperText={helperText}
                    size={size}
                    slotProps={{
                        ...textFieldSlotProps,
                        htmlInput: {
                            style: {
                                height: '20px',
                                padding: size === 'large' ? '4px' : '0px 8px',
                            },
                            ...textFieldSlotProps.htmlInput,
                            // On the input, which screen readers announce; Autocomplete's root is a plain `div`.
                            ...(ariaLabel !== undefined && { 'aria-label': ariaLabel }),
                            ...(ariaRequired !== undefined && { 'aria-required': ariaRequired }),
                        },
                        input: { ...textFieldSlotProps.input, ...inputSlotProps },
                    }}
                />
            )}
            onInputChange={(_, inputValue, reason) => {
                setUserTyped(reason === 'input'); // Only set to true when user types
            }}
            filterOptions={filterOptions}
            renderOption={
                isPresent(renderOption)
                    ? ({ key, ...props }, option, state) => (
                          <li key={key} {...props}>
                              {renderOption(option, state)}
                          </li>
                      )
                    : ({ key, ...props }, option, { inputValue }) => {
                          const label = getOptionLabel?.(option) ?? JSON.stringify(option);
                          const subLabel = getOptionSublabel?.(option);
                          const badge = getOptionBadge?.(option);

                          return (
                              <li key={key} {...props}>
                                  {
                                      <Flexbox
                                          sx={{
                                              justifyContent: 'space-between',
                                              width: '100%',
                                              alignItems: 'flex-start',
                                          }}
                                      >
                                          <Flexbox
                                              gap={2}
                                              style={{ flex: 1, minWidth: 0 }}
                                              sx={{
                                                  flexDirection: 'column',
                                              }}
                                          >
                                              {isPresent(label) && (
                                                  <Highlight
                                                      label={label}
                                                      matcher={userTyped ? [inputValue] : []}
                                                      overrides={{
                                                          Container: HighLightLabelContainer,
                                                      }}
                                                  />
                                              )}
                                              {isPresent(subLabel) && (
                                                  <Highlight
                                                      label={subLabel}
                                                      matcher={userTyped ? [inputValue] : []}
                                                      overrides={{
                                                          Container: HighLightSubLabelContainer,
                                                      }}
                                                  />
                                              )}
                                          </Flexbox>
                                          {badge && <Flexbox style={{ flexShrink: 0, marginLeft: 8 }}>{badge}</Flexbox>}
                                      </Flexbox>
                                  }
                              </li>
                          );
                      }
            }
            slots={{
                ...virtualizedProps,
                paper: action
                    ? (props) => (
                          <Paper {...props} style={{ position: 'relative' }}>
                              {props.children}
                              <Divider />
                              <Flexbox
                                  onMouseDown={(e) => e.preventDefault()}
                                  onClick={(e) => e.stopPropagation()}
                                  sx={{
                                      justifyContent: 'space-between',
                                      width: '100%',
                                      padding: '4px',
                                  }}
                              >
                                  {action.link !== undefined ? (
                                      <TertiaryButton
                                          size="small"
                                          startIcon={<PlusIcon />}
                                          disabled={action.disabled}
                                          {...asLinkProps(action.link)}
                                      >
                                          {action.label}
                                      </TertiaryButton>
                                  ) : (
                                      <TertiaryButton
                                          size="small"
                                          startIcon={<PlusIcon />}
                                          onClick={action.onClick}
                                          disabled={action.disabled}
                                      >
                                          {action.label}
                                      </TertiaryButton>
                                  )}
                              </Flexbox>
                          </Paper>
                      )
                    : undefined,
            }}
            autoHighlight={true}
            popupIcon={<ChevronDownIcon size={24} />}
            getOptionLabel={getOptionLabel}
            renderGroup={DefaultRenderGroup}
            slotProps={autocompleteSlotProps}
            {...omit(rest, 'inputRef')}
        />
    );
}

const HighLightLabelContainer = (props: React.PropsWithChildren<{}>) => (
    <Text variant={'body-small'} color={colorSystem.neutral[9]}>
        {props.children}
    </Text>
);
const HighLightSubLabelContainer = (props: React.PropsWithChildren<{}>) => (
    <Text variant={'caption'} color={colorSystem.neutral[6]} showEllipsis>
        {props.children}
    </Text>
);

export const FieldSelect = React.forwardRef(FieldSelectInner) as <TValue>(
    props: FieldSelectProps<TValue> & { ref?: React.ForwardedRef<unknown> },
) => JSX.Element;
